/**
 * Role-Based Route Protection with Access Tier Model
 * LAYER-IDENTITY-ACCESS
 *
 * Protects routes based on user role and access tier.
 * Uses AuthContext for centralized authentication state.
 *
 * Access Tier Model:
 * - CUSTOMER_TIER: Customer-only access
 * - STAFF_TIER: Case Manager and Compliance can access each other's portals
 * - ADMIN_TIER: Full access to all portals
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth, Role } from '@shared/context';

export type UserRole = 'CUSTOMER' | 'CASE_MANAGER' | 'COMPLIANCE' | 'ADMIN' | 'SUPER_ADMIN';

/**
 * Access Tiers - define which roles can access which areas
 * Staff tier allows cross-portal access between Case Manager and Compliance
 */
export const ACCESS_TIERS = {
  CUSTOMER: ['CUSTOMER'] as UserRole[],
  STAFF: ['CASE_MANAGER', 'COMPLIANCE', 'ADMIN', 'SUPER_ADMIN'] as UserRole[],
  ADMIN: ['ADMIN', 'SUPER_ADMIN'] as UserRole[],
} as const;

export type AccessTier = keyof typeof ACCESS_TIERS;

interface RoleRouteProps {
  /** Roles allowed to access this route */
  allowedRoles?: UserRole[];
  /** Access tier for this route (alternative to allowedRoles) */
  accessTier?: AccessTier;
  /** The component to render if authorized */
  children: React.ReactNode;
  /** Path to redirect to if unauthorized (default: /unauthorized) */
  redirectTo?: string;
}

/**
 * Route guard component that checks user role before rendering
 * Now uses AuthContext for centralized auth state.
 *
 * @example Using allowedRoles:
 * <Route
 *   path="/case-manager/*"
 *   element={
 *     <RoleRoute allowedRoles={['CASE_MANAGER', 'ADMIN']}>
 *       <CaseManagerRoutes />
 *     </RoleRoute>
 *   }
 * />
 *
 * @example Using accessTier:
 * <Route
 *   path="/staff/*"
 *   element={
 *     <RoleRoute accessTier="STAFF">
 *       <StaffPortal />
 *     </RoleRoute>
 *   }
 * />
 */
export function RoleRoute({
  allowedRoles,
  accessTier,
  children,
  redirectTo = '/unauthorized',
}: RoleRouteProps) {
  const location = useLocation();
  const { isAuthenticated, isLoading, user, hasRole } = useAuth();

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner" aria-label="Loading..." />
      </div>
    );
  }

  // Check authentication first
  if (!isAuthenticated || !user) {
    // Save intended path for redirect after login
    return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
  }

  // Determine allowed roles from either prop
  const rolesAllowed: UserRole[] = allowedRoles || (accessTier ? ACCESS_TIERS[accessTier] : []);

  // If no roles specified, deny access
  if (rolesAllowed.length === 0) {
    console.warn('RoleRoute: No allowedRoles or accessTier specified');
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has any of the allowed roles (using role hierarchy from AuthContext)
  const hasAccess = rolesAllowed.some((role) => hasRole(role as Role));

  // Debug logging - remove after fixing
  console.log('[RoleRoute] Check:', {
    path: location.pathname,
    userRoles: user?.roles,
    rolesAllowed,
    hasAccess,
  });

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Get the default route for a given role
 */
export function getDefaultRouteForRole(role: UserRole): string {
  switch (role) {
    case 'CUSTOMER':
      return '/accounts/me';
    case 'CASE_MANAGER':
      return '/case-manager/dashboard';
    case 'COMPLIANCE':
      return '/compliance/queue';
    case 'ADMIN':
    case 'SUPER_ADMIN':
      return '/admin/dashboard';
    default:
      return '/accounts/me';
  }
}

/**
 * Get accessible portals for a role
 * Uses access tier model for staff cross-portal access
 */
export function getAccessiblePortals(role: UserRole): string[] {
  switch (role) {
    case 'CUSTOMER':
      return ['customer'];
    case 'CASE_MANAGER':
      // Staff tier: Case Manager can access Compliance portal
      return ['case-manager', 'compliance'];
    case 'COMPLIANCE':
      // Staff tier: Compliance can access Case Manager portal
      return ['compliance', 'case-manager'];
    case 'ADMIN':
      // Admin tier: Access to staff portals + admin
      return ['admin', 'case-manager', 'compliance'];
    case 'SUPER_ADMIN':
      // Super Admin: Full access including customer view
      return ['admin', 'case-manager', 'compliance', 'customer'];
    default:
      return [];
  }
}

export default RoleRoute;
